import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from "react-router";
import { useApi } from "./api";
import { signOutUrl, type PortalConfig } from "./config";
import { HeaderSearch } from "./HeaderSearch";
import { PrintSlips } from "./pages/PrintSlips";
import { PrintInvoices } from "./pages/PrintInvoices";
import { CustomerPage, CustomersPage } from "./pages/CustomersPage";
import { FollowUpsPage } from "./pages/FollowUpsPage";
import { ServicesPage } from "./pages/ServicesPage";
import { LeadsPage } from "./pages/LeadsPage";
import { TicketPage } from "./pages/TicketPage";
import { TicketsPage } from "./pages/TicketsPage";
import type { Lookups } from "./types";
import { DialogHost } from "./confirm";
import { GalleryPage } from "./pages/GalleryPage";
import { AnalyticsPage } from "./pages/reports/AnalyticsPage";
import { ExpensesPage } from "./pages/reports/ExpensesPage";
import { PaymentsPage } from "./pages/reports/PaymentsPage";
import { WatchmakersPage } from "./pages/reports/WatchmakersPage";

export function App({ config }: { config: PortalConfig }) {
  const auth = useAuth();
  // 30-day sign-in (owner, 2026-11-04): an expired session with a refresh token is renewed quietly, no code needed.
  const [renewing, setRenewing] = useState(false);
  const canRenew = !auth.isLoading && !auth.isAuthenticated && !auth.error && !!auth.user?.refresh_token;
  useEffect(() => {
    if (!canRenew || renewing) return;
    setRenewing(true);
    auth.signinSilent().catch(() => { void auth.removeUser(); }).finally(() => setRenewing(false));
  }, [canRenew]); // eslint-disable-line react-hooks/exhaustive-deps

  if (auth.isLoading || renewing || canRenew) return <Centered config={config}><p>Loading…</p></Centered>;

  if (auth.error) {
    return (
      <Centered config={config}>
        <p className="error">Sign-in problem: {auth.error.message}</p>
        <button onClick={() => void auth.signinRedirect()}>Try again</button>
      </Centered>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <Centered config={config}>
        <p>Staff only. Sign in with your portal account.</p>
        <button
          className="primary"
          // Come back to the page that was asked for (e.g. a ticket link) after signing in.
          onClick={() => void auth.signinRedirect({ state: window.location.pathname + window.location.search })}
        >
          Sign in
        </button>
      </Centered>
    );
  }

  return (
    <BrowserRouter>
      <DialogHost />
      <Routes>
        <Route path="/print/invoices" element={<PrintInvoices config={config} />} />
        <Route path="/print/checkin/:ticketNo" element={<PrintSlips config={config} />} />
        <Route path="*" element={<Portal config={config} />} />
      </Routes>
    </BrowserRouter>
  );
}

function Portal({ config }: { config: PortalConfig }) {
  const auth = useAuth();
  const lookups = useApi<Lookups>(config, "/lookups");
  const location = useLocation();
  const email = auth.user?.profile.email ?? "";

  return (
    <div className="app">
      <header className="topbar">
        <Link to="/dashboard" className="brand">Watch Repair Pros</Link>
        <nav>
          <Link to="/dashboard" className={location.pathname.startsWith("/dashboard") ? "active" : ""}>Dashboard</Link>
          <Link to="/tickets" className={location.pathname.startsWith("/tickets") ? "active" : ""}>Tickets</Link>
          <Link to="/leads" className={location.pathname.startsWith("/leads") ? "active" : ""}>Leads</Link>
          <Link to="/customers" className={location.pathname.startsWith("/customers") ? "active" : ""}>Customers</Link>
          <Link to="/watchmakers" className={location.pathname.startsWith("/watchmakers") ? "active" : ""}>Watchmakers</Link>
          <Link to="/gallery" className={location.pathname.startsWith("/gallery") ? "active" : ""}>Gallery</Link>
          <Link to="/services" className={location.pathname.startsWith("/services") ? "active" : ""}>Services</Link>
          <Link to="/payments" className={location.pathname.startsWith("/payments") ? "active" : ""}>Payments</Link>
          <Link to="/expenses" className={location.pathname.startsWith("/expenses") ? "active" : ""}>Expenses</Link>
          <Link to="/follow-ups" className={location.pathname.startsWith("/follow-ups") ? "active" : ""}>Follow-ups</Link>
        </nav>
        <HeaderSearch config={config} />
        <div className="who">
          <span className="muted" title={`Version ${config.version}`}>{email}</span>
          <button
            onClick={() => {
              void auth.removeUser().then(() => {
                window.location.href = signOutUrl(config, window.location.origin);
              });
            }}
          >
            Sign out
          </button>
        </div>
      </header>
      <main className="content">
        {lookups.error && <p className="error">Could not load statuses: {lookups.error.message}</p>}
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<AnalyticsPage config={config} />} />
          <Route path="/payments" element={<PaymentsPage config={config} />} />
          <Route path="/expenses" element={<ExpensesPage config={config} />} />
          <Route path="/gallery" element={<GalleryPage config={config} lookups={lookups.data} />} />
          <Route path="/watchmakers" element={<WatchmakersPage config={config} />} />
          <Route path="/tickets" element={<TicketsPage config={config} lookups={lookups.data} />} />
          <Route path="/tickets/:ticketNo" element={<TicketPage config={config} lookups={lookups.data} />} />
          <Route path="/follow-ups" element={<FollowUpsPage config={config} lookups={lookups.data} />} />
          <Route path="/services" element={<ServicesPage config={config} />} />
          <Route path="/leads" element={<LeadsPage config={config} lookups={lookups.data} />} />
          {/* Mailers live in Leads now (owner, 2026-11-04). */}
          <Route path="/mailers" element={<Navigate to="/leads?tab=todo" replace />} />
          <Route path="/customers" element={<CustomersPage config={config} />} />
          <Route path="/customers/:id" element={<CustomerPage config={config} />} />
          <Route path="*" element={<p>Page not found. <Link to="/tickets">Go to tickets</Link></p>} />
        </Routes>
      </main>
    </div>
  );
}

function Centered({ config, children }: { config: PortalConfig; children: React.ReactNode }) {
  return (
    <main className="page">
      <header className="centered-head">
        <h1>Watch Repair Pros</h1>
        <span className="muted">Staff portal · {config.version.slice(0, 7)}</span>
      </header>
      {children}
    </main>
  );
}
